import { ApolloClient } from 'apollo-client'
import { InMemoryCache, IntrospectionFragmentMatcher, defaultDataIdFromObject } from 'apollo-cache-inmemory'
import { createPersistedQueryLink } from 'apollo-link-persisted-queries'
import { RestLink } from 'apollo-link-rest'
import { HttpLink } from 'apollo-link-http'
import { onError } from 'apollo-link-error'
import { ApolloLink } from 'apollo-link'
import typeDefs from './typeDefs.gql'
import resolvers from './resolvers'
import defaults from './defaults'
import { development } from '~/lib/helpers/environment'
import introspectionQueryResultData from '~/queries/fragments/fragmentTypes.json'
import { QueryDefaults } from '~/queries/client.gql'

// Add Headers polyfill
if (global.Headers == null) {
  global.Headers = require('fetch-headers')
}

let apolloClient = null

// Creates the Apollo client network interface
function create ({ initialState, ctx }) {
  // Init link options
  const httpLinkOptions = {
    uri: `${process.env.API_BASE_URL}/graphql`,
    headers: {
      'X-Requested-With': 'ajax'
    },
    credentials: 'include'
  }

  // Add cookie from request if on server
  if (ctx && ctx.req && ctx.req.headers && ctx.req.headers.cookie) {
    httpLinkOptions.headers.Cookie = ctx.req.headers.cookie
  }

  // Create http link
  const httpLink = createPersistedQueryLink({ useGETForHashedQueries: false }).concat(new HttpLink(httpLinkOptions))

  // Create unauthenticated link
  const errorLink = onError(({ networkError }) => {
    if (networkError && networkError.statusCode === 401) {
      if (ctx && ctx.res) {
        const url = `${process.env.API_BASE_URL}/?relay=${encodeURI(`${development ? 'http' : 'https'}://${ctx.req.headers.host}${ctx.req.url}`)}`

        ctx.res.end(`
          <!DOCTYPE HTML>
          <html lang="en-US">
            <head>
              <meta charset="utf-8">
              <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1">
              <meta http-equiv="refresh" content="0; url=${url}">
              <script type="text/javascript">
                setTimeout(function () {
                  document.getElementById('redirect').style.display = 'block'
                }, 1500)
              </script>
              <title>mySCP</title>
            </head>
            <body><a id='redirect' style='display: none' href='${url}'>If you are not redirected automatically, please click here.</a></body>
          </html>
        `)
        ctx.res.finished = true
      } else {
        window.location.assign(`${process.env.API_BASE_URL}/login?relay=${encodeURI(window.location.href)}`)
        throw networkError
      }
    }
  })

  const restLinkOptions = {
    uri: process.env.API_BASE_URL,
    endpoints: {
      public: process.env.API_BASE_URL
    },
    headers: {
      'X-Requested-With': 'ajax'
    }
  }

  const restLink = new RestLink(restLinkOptions)

  // Create Apollo link
  const link = ApolloLink.from([
    restLink,
    errorLink,
    httpLink
  ])

  const fragmentMatcher = new IntrospectionFragmentMatcher({ introspectionQueryResultData })

  // Create Apollo cache
  const cache = new InMemoryCache({
    fragmentMatcher,
    dataIdFromObject: object => {
      switch (object.__typename) {
        case 'MdrReport': return object.uuid // use the `uuid` field as the identifier for `MdrReport` type
        default: return defaultDataIdFromObject(object) // fall back to default handling
      }
    }
  }).restore(initialState || {})

  // Create the client
  const client = new ApolloClient({
    link,
    cache,
    connectToDevTools: process.browser,
    ssrMode: !process.browser,
    typeDefs,
    resolvers
  })

  // Write client defaults
  cache.writeQuery({
    query: QueryDefaults,
    data: defaults
  })

  return client
}

// Return new Apollo client according to if SSR or not
export default function init ({ initialState, ctx }) {
  // Create a new client for every server-side request
  if (!process.browser) {
    return create({ initialState, ctx })
  }

  // Reuse client on the client-side
  if (!apolloClient) {
    apolloClient = create({ initialState, ctx })
  }

  return apolloClient
}
